import { useEffect, useState } from 'react'
import Button from '@mui/material/Button'
import MenuItem from '@mui/material/MenuItem'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import { formableIntents } from '../model/manifest'
import { IntentForm } from './IntentForm'
import type { EditorProps } from './types'
import { shown } from '../api/useIntent'

// Grid: columns, gap, and each cell's span and alignment.
//
// **A list of cells with their spans as values — never a drawn grid.**
// The resemblance to MUI's own grid is exactly the trap: the platform
// stores layout *intent* and holds no CSS, and a theme decides what a
// span of 8 out of 12 looks like. Laying these out in columns here would
// make MUI's vocabulary the vocabulary the platform stores, and would
// show the author a rendering this admin has no standing to claim.
//
// Anything the type declares that this editor has no section for is drawn
// from its payload anyway, on the terms `FormEditor` falls through on: a
// grid's name is one field, and a hand-written editor that only knew
// about its layout left it unreachable.

/** The intents this editor sends itself. Everything else falls through. */
const BUILT = new Set(['grid.layout_set', 'grid.cell_configured'])

interface Cell {
  link: string
  span?: number
  align?: string
}

const ALIGNMENTS = ['', 'start', 'center', 'end', 'stretch']
const GAPS = ['', 'none', 'sm', 'md', 'lg']

export function GridEditor({ fields, manifest, typeName, register, busy, send }: EditorProps) {
  const rest = formableIntents(manifest, typeName, fields).filter((intent) => !BUILT.has(intent.name))
  const storedColumns = typeof fields.columns === 'number' ? fields.columns : 12
  const storedGap = typeof fields.gap === 'string' ? fields.gap : ''
  const cells = Array.isArray(fields.cells) ? (fields.cells as Cell[]) : []

  const [columns, setColumns] = useState(String(storedColumns))
  const [gap, setGap] = useState(storedGap)
  useEffect(() => setColumns(String(storedColumns)), [storedColumns])
  useEffect(() => setGap(storedGap), [storedGap])

  const layoutDirty = columns !== String(storedColumns) || gap !== storedGap
  const parsedColumns = Number.parseInt(columns, 10)

  return (
    <Stack spacing={3}>
      {rest.map((intent) => (
        <IntentForm
          key={intent.name}
          intent={intent}
          fields={fields}
          holder={typeName}
          showName={register === 'build'}
          busy={busy}
          send={send}
        />
      ))}

      <Stack spacing={1.5}>
        <Typography variant="h3">Layout</Typography>
        <Stack direction="row" spacing={1.5}>
          <TextField
            label="Columns"
            value={columns}
            onChange={(event) => setColumns(event.target.value)}
            type="number"
            sx={{ width: 140 }}
          />
          <TextField select label="Gap" value={gap} onChange={(event) => setGap(event.target.value)} sx={{ width: 160 }}>
            {GAPS.map((value) => (
              <MenuItem key={value || 'unset'} value={value}>
                {value || '(unset)'}
              </MenuItem>
            ))}
          </TextField>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            disabled={!layoutDirty || Number.isNaN(parsedColumns) || busy}
            onClick={() => send('grid.layout_set', { columns: parsedColumns, gap }).catch(shown)}
          >
            Set layout
          </Button>
          <Button
            disabled={!layoutDirty || busy}
            onClick={() => {
              setColumns(String(storedColumns))
              setGap(storedGap)
            }}
          >
            Discard
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          A column count and a gap name are layout <em>intent</em>. A theme decides what they render as; this admin
          deliberately does not draw them.
        </Typography>
      </Stack>

      <Stack spacing={1.5}>
        <Typography variant="h3">Cells</Typography>
        {cells.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No cells placed.
          </Typography>
        ) : (
          cells.map((cell) => <CellRow key={cell.link} cell={cell} columns={storedColumns} busy={busy} send={send} />)
        )}
      </Stack>
    </Stack>
  )
}

function CellRow({
  cell,
  columns,
  busy,
  send,
}: {
  cell: Cell
  columns: number
  busy: boolean
  send: EditorProps['send']
}) {
  const [span, setSpan] = useState(String(cell.span ?? ''))
  const [align, setAlign] = useState(cell.align ?? '')
  useEffect(() => setSpan(String(cell.span ?? '')), [cell.span])
  useEffect(() => setAlign(cell.align ?? ''), [cell.align])

  const dirty = span !== String(cell.span ?? '') || align !== (cell.align ?? '')
  const parsedSpan = span === '' ? 0 : Number.parseInt(span, 10)

  return (
    <Paper sx={{ p: 1.5 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap="wrap" useFlexGap>
        <Typography variant="body2" sx={{ fontFamily: 'ui-monospace, monospace', minWidth: 90 }}>
          {cell.link}
        </Typography>
        <TextField
          label="Span"
          size="small"
          value={span}
          onChange={(event) => setSpan(event.target.value)}
          type="number"
          sx={{ width: 110 }}
          helperText={`of ${columns}`}
        />
        <TextField
          select
          size="small"
          label="Align"
          value={align}
          onChange={(event) => setAlign(event.target.value)}
          sx={{ width: 150 }}
        >
          {ALIGNMENTS.map((value) => (
            <MenuItem key={value || 'unset'} value={value}>
              {value || '(unset)'}
            </MenuItem>
          ))}
        </TextField>
        <Button
          size="small"
          variant="contained"
          disabled={!dirty || Number.isNaN(parsedSpan) || busy}
          onClick={() => send('grid.cell_configured', { link: cell.link, span: parsedSpan, align }).catch(shown)}
        >
          Configure
        </Button>
      </Stack>
    </Paper>
  )
}
